<template>
  <p
    v-if="!!readonly"
    class="InputText__readonly"
    :data-testid="`${id}-input-text-readonly`"
    :empty="(!modelValue && !$slots.default) || null"
  >
    <slot>
      <span>{{ modelValue ? modelValue : readonlyPlaceholder }}</span>
    </slot>
    <span v-if="showMetrics" class="InputText__readonly__metrics" :data-testid="`${id}-input-text-metrics`">
      <NovaTextMetrics
        :metric-type="metricType"
        :words-count="wordsCount"
        :characters-count="charactersCount"
        :max-characters="Number(inputProps?.maxlength?.valueOf()) || maxLength"
      ></NovaTextMetrics>
    </span>
  </p>

  <span v-else :style="{ width: '100%' }">
    <label v-if="label" :data-testid="`${id}-input-text-label`" :for="`${id}-label`" class="InputText__label">
      {{ label }}
    </label>
    <div
      class="InputText"
      :class="[flatEdgeClasses, squareEdgeClasses]"
      :disabled="disabled || null"
      :theme="(error ? 'error' : theme) || null"
      :size="size"
      :data-testid="`${id}-input-text-container`"
      :data-invalid="isInvalid === false ? undefined : true"
    >
      <nova-icon v-if="leftIcon" name="search" :size="18" />
      <input
        :id="id"
        ref="inputRef"
        type="text"
        class="InputText__input"
        :data-testid="`${id}-input-text`"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        v-bind="inputProps"
        @input="handleInput"
        @click="emits('click')"
        @keydown.tab="handleTab"
        @keydown.enter.prevent
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <button
        v-if="modelValue && !disabled"
        type="button"
        tabindex="-1"
        class="InputText__clear-btn"
        :data-testid="`${id}-input-text-clear-btn`"
        v-bind="buttonClearProps"
        @click="handleClear"
      >
        <NovaIcon name="close" :size="8" />
      </button>
    </div>
    <div v-if="error || showMetrics" class="flex justify-between items-center mt-1 w-full">
      <span
        v-if="error"
        class="InputText__error-msg text-error-110 font-medium text-xs"
        :data-testid="`${id}-input-text-error`"
        >{{ error }}</span
      >
      <span
        v-if="showMetrics"
        class="InputText__metrics ml-auto shrink-0 self-start"
        :data-testid="`${id}-input-text-metrics`"
      >
        <NovaTextMetrics
          :metric-type="metricType"
          :words-count="wordsCount"
          :characters-count="charactersCount"
          :max-characters="Number(inputProps?.maxlength?.valueOf()) || maxLength"
        ></NovaTextMetrics>
      </span>
    </div>
  </span>
</template>

<script lang="ts" setup>
import NovaIcon from "@/ui-kit/NovaIcon/NovaIcon.vue";
import NovaTextMetrics from "../NovaTextMetrics/NovaTextMetrics.vue";
import { InputHTMLAttributes } from "vue";

export interface Props {
  id?: string;
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  leftIcon?: "search";
  theme?: "success" | "error" | "warning" | "default";
  error?: string;
  label?: string;
  size?: "sm" | "md" | "lg";
  isInvalid?: boolean;
  readonly?: boolean;
  readonlyPlaceholder?: string;
  buttonClearProps?: Record<string, unknown>;
  inputProps?: InputHTMLAttributes;
  showMetrics?: boolean;
  metricType?: "words" | "characters";
  maxLength?: number;
  /**
   * Edges whose border and corner radius are removed, so the field can sit flush
   * against a neighbor and share a single seam. Agnostic of what the neighbor is.
   */
  flatEdges?: ("top" | "right" | "bottom" | "left")[];
  /**
   * Edges whose corner radius is removed but whose border is kept. Use on the side
   * that should draw (and highlight) the shared seam next to a flattened neighbor.
   */
  squareEdges?: ("top" | "right" | "bottom" | "left")[];
}

interface Events {
  (e: "update:modelValue", value: string): void;
  (e: "click"): void;
  (e: "clear"): void;
  (e: "tab", event: KeyboardEvent): void;
  (e: "focus"): void;
  (e: "blur"): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "Insert text",
  readonlyPlaceholder: "This field is empty...",
  flatEdges: () => [],
  squareEdges: () => [],
});
const emits = defineEmits<Events>();

const flatEdgeClasses = computed(() => props.flatEdges.map((edge) => `InputText--flat-${edge}`));
const squareEdgeClasses = computed(() => props.squareEdges.map((edge) => `InputText--square-${edge}`));

const wordsCount = computed(() => {
  return (
    props.modelValue
      ?.trim()
      .split(" ")
      .filter((token) => token.length > 0).length ?? 0
  );
});

const charactersCount = computed(() => {
  return props.modelValue?.length ?? 0;
});

const handleInput = (event: Event) => {
  if (event.target) {
    const { value } = event.target as HTMLInputElement;
    emits("update:modelValue", value);
  }
};

const handleClear = () => {
  emits("update:modelValue", "");
  emits("clear");
};

const inputRef = ref<HTMLInputElement | null>(null);

function handleTab(event: KeyboardEvent) {
  emits("tab", event);
}

function handleFocus() {
  emits("focus");
}

function handleBlur() {
  emits("blur");
}

function focus() {
  inputRef.value?.focus();
}

defineExpose({ focus });
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities";

.InputText {
  --border-color: var(--color-grey-100);

  width: 100%;
  padding: 0 rem(5);
  border: var(--border-default);
  border-color: var(--border-color);
  border-radius: var(--border-radius-default);
  position: relative;
  display: flex;
  align-items: center;
  background-color: white;

  &[data-invalid] {
    border-color: var(--color-error-100);
  }

  & > svg {
    flex-shrink: 0;
  }

  &__input {
    margin-left: rem(5);
    height: rem(28);
    border: none;
    outline: none;
    width: 100%;
    background-color: transparent;
    color: var(--color-text-100);

    @include font-regular(14);

    &::placeholder {
      font-style: italic;
      color: var(--color-text-70);
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &:hover {
    --border-color: var(--color-text-80);
  }

  &:active {
    --border-color: var(--color-primary-100);

    // Win the stacking order over an overlapping neighbor (e.g. a segmented
    // control seam) so this highlighted border paints on top.
    z-index: 1;
  }

  &:focus-within {
    --border-color: var(--color-primary-100);

    z-index: 1;
  }

  &[disabled] {
    background: #f9f9fb;

    &:hover {
      border-color: var(--color-grey-100);
    }

    & input {
      color: var(--color-neutral-80);
    }
  }

  &[theme="error"] {
    --border-color: var(--color-error-100);
  }

  &[theme="success"] {
    --border-color: var(--color-success-100);
  }

  &[theme="warning"] {
    --border-color: var(--color-warning-100);
  }

  &__label {
    position: relative;
    bottom: 4px;

    @include font-regular(12);

    color: var(--color-text-100);
  }

  &__clear-btn {
    background: var(--color-neutral-40);
    border: none;
    border-radius: 100%;
    cursor: pointer;
    padding: rem(8);
    position: relative;
    margin-right: 5px;

    & > svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &__error-icon {
    color: var(--color-error-100);
    padding: rem(5) rem(3);
  }

  &__success-icon {
    color: var(--color-success-100);
    padding: rem(5) rem(3);
  }

  &[size="sm"] {
    height: 32px;
  }

  &[size="md"] {
    padding: 1px rem(7);
  }

  &[size="lg"] {
    padding: 5px rem(7);
  }

  &__readonly {
    @include font-regular(14);

    &[empty] {
      font-style: italic;
      color: var(--color-text-70);
    }
  }

  // Flatten a given edge so the field can share a seam with a neighbor: remove
  // that border and square the two corners on it. The border removal must beat
  // the hover/focus/theme states above, which only ever set `--border-color`,
  // so `border-*: none` here always wins.
  &--flat-top {
    border-top: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  &--flat-right {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &--flat-bottom {
    border-bottom: none;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &--flat-left {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  // Square a corner while keeping the border, so this edge can draw (and, on
  // focus, highlight) the shared seam next to a flattened neighbor.
  &--square-top {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  &--square-right {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &--square-bottom {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &--square-left {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}
</style>
